// HouseholdList.tsx — Inline-Liste für Beziehungen zu Personen. Über das
// `sameHousehold`-Prop wird sie sowohl für „Haushalt" (true) als auch für
// „weitere Beziehungen zu Personen" (false) genutzt. Pro Eintrag: Person
// (person-only) + die konkrete, gegenderte Beziehung DIREKT aus
// RELATIONSHIP_CATALOG (Ehemann/Ehefrau, Vater/Mutter …). „Sonstige (Freitext)"
// → zwei Richtungs-Felder (was other für self ist / was self für other ist).

import * as React from "react";
import { FieldRow, LabeledSelect, LabeledInput } from "../forms/formPrimitives";
import { EintragsListe, type EintragsListeSteuerung } from "../forms/EintragsListe";
import { beziehungsLabel, refName, zeilenAnzeigeVon } from "./beziehungsAnzeige";
import { ContactRefField } from "./ContactRefField";
import { RELATIONSHIP_CATALOG, RELATIONSHIP_FREE } from "../../utils/householdRole";
import { useRelStyles } from "./styles";
import { uid } from "./types";
import type { HouseholdItem, ContactRef } from "./types";
import { ZeilenAktion } from "../forms/BeziehungsTabelle";

const HausIcon = (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 9.5L10 3.5l7 6M5 8v8.5h10V8M8.5 16.5v-4.5h3v4.5" />
  </svg>
);

const RausIcon = (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 9.5L10 3.5l4 3.4M5 8v8.5h6M12.5 13.5h5M15.5 11l2.5 2.5-2.5 2.5" />
  </svg>
);

/**
 * Arten der ziehbaren Zeilen (User 28.09.2026): Weitere Beziehungen → Haushalt
 * und zurück. Je Richtung eine eigene Art, damit eine Liste ihre eigenen
 * Zeilen nicht als Ablage anbietet.
 */
export const ZIEH_ART_PERSON = "beziehung-person";
export const ZIEH_ART_HAUSHALT = "haushalt-person";

export function HouseholdList({
  value,
  onChange,
  sameHousehold,
  inDenHaushalt,
  ausDemHaushalt,
  ablage,
  nachnameVorschlag,
  steuerung,
}: {
  value: HouseholdItem[];
  onChange: (next: HouseholdItem[]) => void;
  /** true = Haushalt-Sektion, false = weitere Beziehungen zu Personen. */
  sameHousehold: boolean;
  /**
   * Weitere Beziehungen: Zeilen sind ziehbar, und je Zeile verschiebt ein Knopf
   * den Eintrag in den Haushalt — der Weg ohne Maus.
   */
  inDenHaushalt?: (id: string) => void;
  /** Haushalt: dasselbe in die Gegenrichtung — zu den weiteren Beziehungen. */
  ausDemHaushalt?: (id: string) => void;
  /** Nimmt gezogene Zeilen der jeweils anderen Liste an. */
  ablage?: (id: string) => void;
  /** D4: Nachname des Hauptkontakts — beim Neu-Eintippen vorbelegt. */
  nachnameVorschlag?: string;
  /** Popup von außen öffnen, z. B. mit vorbelegtem Label „Ehefrau" (T3). */
  steuerung?: React.MutableRefObject<EintragsListeSteuerung<HouseholdItem> | null>;
}) {
  const verschieben = sameHousehold ? ausDemHaushalt : inDenHaushalt;
  return (
    <EintragsListe<HouseholdItem>
      items={value}
      onChange={onChange}
      neu={() => ({
        id: uid(),
        ref: null,
        relationLabel: RELATIONSHIP_CATALOG[0],
        sameHousehold,
        freeRole: "",
        freeRoleReverse: "",
      })}
      hinzufuegenLabel="Personen"
      dialogTitel={sameHousehold ? "Person im Haushalt" : "Beziehung außerhalb des Haushalts"}
      zeile={(it) => ({
        titel: refName(it.ref) || "Ohne Namen",
        zusatz: beziehungsLabel(it) || undefined,
        ...zeilenAnzeigeVon(it.ref),
      })}
      leerText={
        ablage
          ? sameHousehold
            ? "Noch keine Personen erfasst — Personen aus „Beziehungen außerhalb des Haushaltes“ lassen sich hierher ziehen."
            : "Noch keine Personen erfasst — Personen aus dem Haushalt lassen sich hierher ziehen."
          : "Noch keine Personen erfasst."
      }
      spalten={{ name: "Name", rolle: sameHousehold ? "Rolle / Beziehung" : "Beziehung" }}
      ziehArt={sameHousehold ? ZIEH_ART_HAUSHALT : ZIEH_ART_PERSON}
      ziehbar={!!verschieben}
      ablage={
        ablage
          ? {
              onAblage: ablage,
              art: sameHousehold ? ZIEH_ART_PERSON : ZIEH_ART_HAUSHALT,
              hinweis: sameHousehold
                ? "Loslassen, um die Person in den Haushalt zu übernehmen"
                : "Loslassen, um die Person aus dem Haushalt zu nehmen",
            }
          : undefined
      }
      zeilenExtra={
        verschieben
          ? (it) =>
              sameHousehold ? (
                <ZeilenAktion label="Aus dem Haushalt nehmen" onClick={() => verschieben(it.id)}>
                  {RausIcon}
                </ZeilenAktion>
              ) : (
                <ZeilenAktion label="In den Haushalt verschieben" onClick={() => verschieben(it.id)}>
                  {HausIcon}
                </ZeilenAktion>
              )
          : undefined
      }
      istLeer={(it) => !it.ref}
      steuerung={steuerung}
      renderFelder={(it, update) => (
        <HaushaltPersonFelder
          it={it}
          update={update}
          sameHousehold={sameHousehold}
          nachnameVorschlag={sameHousehold ? nachnameVorschlag : undefined}
        />
      )}
    />
  );
}

/**
 * Die Felder EINER Person mit Beziehung — geteilt zwischen der Liste (Popup)
 * und den Folgefeldern im Status-Schritt (inline, mit Haushalts-Haken).
 */
export function HaushaltPersonFelder({
  it,
  update,
  sameHousehold,
  nachnameVorschlag,
}: {
  it: HouseholdItem;
  update: (patch: Partial<HouseholdItem>) => void;
  sameHousehold: boolean;
  nachnameVorschlag?: string;
}) {
  const s = useRelStyles();
  const isFree = it.relationLabel === RELATIONSHIP_FREE;
  const fwd = isFree ? (it.freeRole ?? "").trim() : it.relationLabel;
  const rev = isFree ? (it.freeRoleReverse ?? "").trim() : "";
  return (
    <>
      <div className={s.refField}>
        <span className={s.sectionLabel}>Person</span>
        <ContactRefField
          wantType="Person"
          personOnly
          showPersonAddress={!sameHousehold}
          sameHousehold={sameHousehold}
          idBase={`hh-${it.id}`}
          nachnameVorschlag={nachnameVorschlag}
          value={it.ref}
          onChange={(ref: ContactRef | null) => update({ ref })}
        />
      </div>
      <FieldRow columns={2}>
        <LabeledSelect
          label="Beziehung"
          name={`hh-rel-${it.id}`}
          value={it.relationLabel}
          onChange={(v) => update({ relationLabel: v })}
          options={[
            ...RELATIONSHIP_CATALOG.map((r) => ({ value: r, label: r })),
            { value: RELATIONSHIP_FREE, label: "Sonstige (Freitext) …" },
          ]}
          placeholder="Beziehung"
        />
        <div />
      </FieldRow>
      {isFree && (
        <FieldRow columns={2}>
          <LabeledInput
            label="Beziehung (andere Person → dieser Kontakt)"
            name={`hh-freefwd-${it.id}`}
            value={it.freeRole ?? ""}
            onChange={(v) => update({ freeRole: v })}
            placeholder="z. B. Patentante"
          />
          <LabeledInput
            label="Beziehung (dieser Kontakt → andere Person)"
            name={`hh-freerev-${it.id}`}
            value={it.freeRoleReverse ?? ""}
            onChange={(v) => update({ freeRoleReverse: v })}
            placeholder="z. B. Patenkind"
          />
        </FieldRow>
      )}
      {fwd && (
        <p className={s.hint}>
          Beziehung: <strong>{fwd}</strong>
          {rev ? (
            <>
              {" "}
              · Gegenrichtung: <strong>{rev}</strong>
            </>
          ) : null}
        </p>
      )}
    </>
  );
}
